import {
	ApolloClientOptions,
	ApolloLink,
	createHttpLink,
	from,
	Operation,
	PossibleTypesMap,
	split
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { Kind, OperationTypeNode } from 'graphql';
import { createClient } from 'graphql-ws';
import { firstValueFrom, map, Observable, shareReplay } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

import { OktaInterfaceService } from '@shure/cloud/shared/okta/data-access';
import { ILogger } from '@shure/shared/angular/utils/logging';

import { ApolloCacheFactory } from './apollo-cache-factory.service';
import { ApolloConnectInfo, ConnectInfoProviderValue } from './apollo-connection.models';
import { apolloErrorContains, nonRetryableErrorSubStrings } from './apollo-utils';

export interface OperationLinkOverride {
	operationName: string;
	link: 'http' | 'ws';
}

export abstract class ApolloConnectionService {
	private retryCountLastSeenOk = 0;
	private retryCountLastSeen = 0;

	private readonly logger: ILogger;
	private apolloConnectInfo$: Observable<ApolloConnectInfo>;

	constructor(
		logger: ILogger,
		private readonly name: string,
		private readonly possibleTypes: PossibleTypesMap,
		private readonly retryCountMax: number,
		private readonly connectInfoProvider$: Observable<ConnectInfoProviderValue>,
		private readonly apolloCacheFactory: ApolloCacheFactory,
		private readonly oktaIntfService: OktaInterfaceService,
		private readonly operationLinkOverrides: OperationLinkOverride[] = []
	) {
		this.logger = logger.createScopedLogger(`ApolloConnectionService.${this.name}`);
		this.apolloConnectInfo$ = this.connectInfoProvider$.pipe(
			map((connectInfo: ConnectInfoProviderValue) => {
				const apolloConnectInfo: ApolloConnectInfo = {
					gqlHttpUrl: connectInfo.url,
					// Use websocket URL if provided. Otherwise, use the HTTP URL with the protocol changed to ws/wss.
					gqlWsUrl: connectInfo.wsUrl ?? connectInfo.url.replace('https', 'wss').replace('http', 'ws'),
					apiKey: connectInfo.apiKey,
					useWsTransport: connectInfo.useWsTransport
				};
				// so that when testing, we have a record in the output which BE was used.
				this.logger.debug('constructor', 'apolloConnectionInfo', JSON.stringify(apolloConnectInfo));
				return apolloConnectInfo;
			}),
			shareReplay(1)
		);
		this.doResetRetryCount();
	}

	public createApolloClientOptions(): ApolloClientOptions<unknown> {
		return {
			cache: this.apolloCacheFactory.createCache(this.name, this.possibleTypes),
			link: this.createLink()
		};
	}

	protected createLink(): ApolloLink {
		this.logger.trace('createLink()', '', { name: this.name });

		const wsLink = this.createWsLink();
		const httpLink = this.createHttpLink();

		return from([
			this.createRetryLink(),
			this.createSuccessLink(),
			this.createErrorLink(),
			this.createContextConnectInfo(),
			this.createSplitLink(wsLink, httpLink)
		]);
	}

	protected createContextConnectInfo(): ApolloLink {
		return setContext(async () => {
			const connectInfo = await firstValueFrom(this.apolloConnectInfo$);
			return { connectInfo };
		});
	}

	protected createSuccessLink(): ApolloLink {
		// If data is coming back, we have verified that the connection to sysapi server is ok
		// and we can reset the retry counter.
		return new ApolloLink((operation, forward) => {
			operation.setContext(({ headers = {} }) => ({
				headers: {
					...headers,
					Authorization: `Bearer ${this.oktaIntfService.getAccessTokenJWT()}`
				}
			}));
			return forward(operation).map((data) => {
				this.doResetRetryCount();
				return data;
			});
		});
	}

	protected createErrorLink(): ApolloLink {
		return onError(({ networkError }) => {
			if (networkError) {
				// Catches http error codes, we are ignore this for now,
				// but could indicate authentication issues if used properly on server side
				if ('statusCode' in networkError) {
					this.logger.trace('errorLink', 'httpError', { statusCode: networkError.statusCode });
					return;
				}
				this.logger.trace('errorLink', 'networkError', JSON.stringify(networkError));
				this.onNetworkError();
			}
		});
	}

	protected createRetryLink(): ApolloLink {
		// for Details of the Retry Link
		// https://www.apollographql.com/docs/react/api/link/apollo-link-retry/#overview
		// https://www.apollographql.com/docs/react/api/link/apollo-link-retry/#avoiding-thundering-herd
		return new RetryLink({
			attempts: {
				retryIf: (error, operation: Operation): boolean => {
					if (apolloErrorContains(error, nonRetryableErrorSubStrings)) {
						return false;
					}
					const opDefs = operation.query.definitions[0];
					if ('operation' in opDefs && opDefs.operation !== 'subscription') {
						return false;
					}
					return !!error;
				}
			},
			delay: (count, operation, error): number => {
				const computedDelay = Math.min(10000, count * 1000 * Math.random());
				const logMessage = `Operation: ${operation.operationName}, Variables: ${
					'variables' in operation ? JSON.stringify(operation.variables, null, 2) : 'n/a'
				}, Error: ${error}, count: ${count}, ComputedDelay: ${computedDelay}`;
				this.logger.error('retryLink()', 'delay()', logMessage);
				return computedDelay;
			}
		});
	}

	protected createWsLink(): ApolloLink {
		let pongTimeout: ReturnType<typeof setTimeout>;

		// https://the-guild.dev/graphql/ws/docs/interfaces/client.ClientOptions
		const client = createClient({
			url: () =>
				firstValueFrom(this.apolloConnectInfo$).then((connectInfo) => {
					const uri = new URL(connectInfo.gqlWsUrl);
					uri.searchParams.set('access_token', this.oktaIntfService.getAccessTokenJWT());
					return uri.toString();
				}),
			lazy: true, // establish on first subscription, tear down on last unsubscribe.
			retryAttempts: 0xffff, // default is 5
			shouldRetry: () => true, // default is to only retry on "close", not errors
			keepAlive: 30_000, // ping every 30 seconds
			generateID: () => uuidv4(),
			on: {
				opened: (_socket: unknown) => {
					this.logger.debug('GraphQLWsLink:createClient', 'websocket opened');
				},
				connected: () => {
					this.logger.debug('GraphQLWsLink:createClient', 'websocket connected');
				},
				error: (error) => {
					this.logger.error('GraphQLWsLink:createClient', 'websocket error', JSON.stringify(error));
				},
				closed: (event) => {
					this.logger.error('GraphQLWsLink:createClient', 'websocket closed', JSON.stringify(event));
				},
				// event when ping is sent or received
				ping: (received: boolean) => {
					if (received) {
						return;
					}
					// note: the actual ping is sent by the library
					pongTimeout = setTimeout(() => {
						// A close event `4499: Terminated` is issued to the current WebSocket and an
						// artificial `{ code: 4499, reason: 'Terminated', wasClean: false }` close-event-like
						// object is immediately emitted without waiting for the one coming from `WebSocket.onclose`
						// Calling terminate is not considered fatal and a connection retry will occur as expected
						// See: https://github.com/enisdenjo/graphql-ws/discussions/290
						this.logger.debug('GraphQLWsLink:createClient', 'timeout waiting for pong');
						client.terminate();
					}, 15_000);
				},
				// event when pong is sent or received
				pong: (received: boolean) => {
					if (received) {
						clearTimeout(pongTimeout);
					}
				}
			}
		});
		return new GraphQLWsLink(client);
	}

	protected createHttpLink(): ApolloLink {
		return createHttpLink({
			uri: (operation) => {
				const { connectInfo } = operation.getContext();
				return connectInfo.gqlHttpUrl;
			}
		});
	}

	/**
	 * Split operation based on type. By default, queries and mutation use HTTP and subscriptions
	 * use Websockets. If useWsTransport is true, all operations use Websockets.
	 * If you need to override this behavior, you can provide a list of operationLinkOverrides.
	 * on a per operation basis. This is useful if there are some operations that you want to force
	 * to either HTTP or WSS regardless of the transport setting. Caveat... make sure the
	 * server supports that operation on the transport you are forcing it to.
	 */
	protected createSplitLink(wsLink: ApolloLink, httpLink: ApolloLink): ApolloLink {
		return split(
			({ query, getContext }) => {
				const definition = getMainDefinition(query);
				const overRide = this.operationLinkOverrides.find((x) => x.operationName === definition.name?.value);
				if (overRide !== undefined) {
					return overRide.link === 'ws' ? true : false;
				}
				const context = getContext();
				// eslint-disable-next-line dot-notation
				const connectInfo: ApolloConnectInfo = context['connectInfo'];
				if (connectInfo.useWsTransport) {
					return true;
				}
				const isSubscription =
					definition.kind === Kind.OPERATION_DEFINITION &&
					definition.operation === OperationTypeNode.SUBSCRIPTION;
				return isSubscription;
			},
			wsLink,
			httpLink
		);
	}

	private onNetworkError(): void {
		this.logger.trace('onNetworkError', '');
		// Currently we are not doing anything, but this indicate that the connection is broken,
		// and we should show this to the user to take action.
	}

	private shouldRetry(count: number, operation: Operation, error: unknown): boolean {
		// count comes from Apollo RetryLink and seems to occasionally being reset,
		// but the reset is not related to e.g. successfully subscriptions
		this.retryCountLastSeen = count;

		const retryCount = this.getRetryCount();
		this.logger.trace('shouldRetry', '', {
			retryCount,
			retryCountLastSeen: this.retryCountLastSeen,
			retryCountLastSeenOk: this.retryCountLastSeenOk,
			count,
			operation,
			error
		});
		return retryCount < this.retryCountMax;
	}

	private getRetryDelay(_count: number): number {
		return 2000 * Math.random();
	}

	private doResetRetryCount(): void {
		this.retryCountLastSeenOk = this.retryCountLastSeen;
	}

	private getRetryCount(): number {
		return Math.max(0, this.retryCountLastSeen - this.retryCountLastSeenOk);
	}
}
